import React, { useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { ContributorData } from './hooks/useProcessedContributorsResult.ts';
import { i18n } from './contributorsMacroI18n.ts';
import { ContributorItem } from './ContributorItem.tsx';

const styles = cssMap({
	showMoreButton: {
		backgroundColor: 'transparent',
		padding: token('space.0'),
	},
});

export interface FlatLayoutProps {
	visibleContributors: ContributorData[];
	hiddenContributors: ContributorData[];
	showCount: boolean;
	showTime: boolean;
}

export const FlatLayout = ({
	visibleContributors,
	hiddenContributors,
	showCount,
	showTime,
}: FlatLayoutProps) => {
	const [showHidden, setShowHidden] = useState(false);
	const { formatMessage } = useIntl();

	const handleShowMore = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			setShowHidden(true);
		},
		[setShowHidden],
	);

	return (
		<Inline as="span" testId="contributors-flat-layout" shouldWrap>
			{visibleContributors.map((contributor, index) => (
				<Inline key={contributor.id}>
					{index > 0 && <FormattedMessage {...i18n.separator} />}
					<ContributorItem contributor={contributor} showCount={showCount} showTime={showTime} />
				</Inline>
			))}
			{hiddenContributors.length > 0 && !showHidden && (
				<Inline alignBlock="end">
					{visibleContributors.length > 0 && <FormattedMessage {...i18n.separator} />}
					<Pressable
						xcss={styles.showMoreButton}
						testId="contributors-show-more"
						onClick={handleShowMore}
						title={formatMessage(i18n.showMoreTooltip, {
							count: hiddenContributors.length,
						})}
					>
						<FormattedMessage {...i18n.showMoreLabel} />
					</Pressable>
				</Inline>
			)}
			{showHidden &&
				hiddenContributors.map((contributor, index) => (
					<Inline key={contributor.id}>
						{(visibleContributors.length > 0 || index > 0) && (
							<FormattedMessage {...i18n.separator} />
						)}
						<ContributorItem contributor={contributor} showCount={showCount} showTime={showTime} />
					</Inline>
				))}
		</Inline>
	);
};
